import type { ErrorCopy } from '@shared/errors/catalog';

/**
 * The `error_code` values only the editorial surfaces meet.
 *
 * Two of them are the two-person rule speaking, and their copy names the rule
 * rather than reading as a generic permission failure: an operator refused on
 * their own post has not lost a permission, they have met the one control the
 * product spends a second operator on.
 *
 * **Never inline a backend code in JSX.** The code is a log key; the sentence
 * is what reaches the operator, and the `X-Request-ID` is what they quote.
 */
/**
 * The address itself is malformed — not an article that was removed.
 *
 * Keyed under a name no service emits, because the condition is decided here:
 * `/editorial/not-a-ulid` cannot name a record, and answering it with «لم يعد
 * هذا المقال موجوداً» tells an operator something was deleted when the truth is
 * that the link they followed is broken. Both answers are safe; only this one is
 * accurate, and only this one names something they can act on.
 */
export const EDITORIAL_MALFORMED_ID = 'EDITORIAL_MALFORMED_ID';

export const editorialErrors: Readonly<Record<string, ErrorCopy>> = {
  [EDITORIAL_MALFORMED_ID]: {
    message: 'هذا العنوان لا يشير إلى مقال. تحقّق من الرابط، أو ارجع إلى قائمة المقالات.',
    tone: 'warning',
  },
  CONTENT_EDITORIAL_POST_NOT_FOUND: {
    message: 'لم يعد هذا المقال موجوداً. أعد تحميل القائمة لقراءتها كما هي الآن.',
    tone: 'warning',
  },
  CONTENT_EDITORIAL_NOT_EDITABLE: {
    message: 'المقال ليس في حالٍ يملك كاتبه تحريرها. المسوّدة والمردود وحدهما يُحرَّران.',
    tone: 'warning',
  },
  CONTENT_EDITORIAL_SELF_REVIEW_FORBIDDEN: {
    message: 'لا يمرّ مقال بيدٍ واحدة: كاتبه لا ينشره ولا يردّه. راجِعٌ آخر هو من يقرّر.',
    tone: 'warning',
  },
  CONTENT_EDITORIAL_REJECTION_REASON_REQUIRED: {
    message: 'اكتب سبب الردّ. يقرأه كاتب المقال، وبغيره يعود إليه يخمّن ما يصلحه.',
    tone: 'warning',
  },
  CONTENT_UNSAFE_EDITORIAL_BODY: {
    message: 'الجسم يحمل عناصر غير مسموح بها. أزل ما أُدخل من مصدر خارجي ثمّ أعد الحفظ.',
    tone: 'warning',
  },
  CONTENT_INVALID_STATE_TRANSITION: {
    message: 'تغيّرت حالة المقال بينما كانت الصفحة مفتوحة. أعد التحميل لتقرأ الحال الحالية.',
    tone: 'warning',
  },
  CONTENT_SLUG_CONFLICT: {
    message: 'هذا العنوان مستعمل بالفعل. غيّره قليلاً ثمّ أعد الحفظ.',
    tone: 'warning',
  },
  CONTENT_FORBIDDEN: {
    message: 'هذا الإجراء يحتاج دور مراجِع تحريري، ولا يحمله حسابك.',
    tone: 'warning',
  },
  CONTENT_BAD_INPUT: {
    message: 'تعذّر إتمام الطلب بالبيانات الحالية. راجع الحقول ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  CONTENT_RATE_LIMITED: {
    message: 'تعديلات كثيرة في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  CONTENT_UPSTREAM_FAILED: {
    message: 'الخدمة غير متاحة الآن. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  CONTENT_UPSTREAM_TIMEOUT: {
    message: 'تأخّرت الخدمة عن الردّ. أعد تحميل الصفحة للاطّلاع على النتيجة.',
    tone: 'neutral',
  },

  // ---- artwork, raised by `backend-assets` on the rail's upload ------------
  //
  // **None of these was catalogued, and the gap was silent.** `resolveErrorCopy`
  // falls through an unknown code to `STATUS_FALLBACK[422]` — «راجع الحقول
  // المعلَّمة ثمّ أعد الإرسال» — which on an image upload names no field, and no
  // field is marked. An operator whose 192×192 file was refused for being below
  // the 1920×1080 floor was told to check fields that do not exist. The refusal
  // was reaching the console all along; what it lacked was a sentence.
  ASSETS_ARTWORK_TOO_SMALL: {
    message: 'أبعاد الصورة أقلّ من الحدّ الأدنى المذكور تحت زرّ الرفع. اختر صورةً أكبر.',
    tone: 'warning',
  },
  ASSETS_UNSUPPORTED_FORMAT: {
    message: 'هذه الصيغة غير مقبولة. راجع الصيغ المذكورة تحت زرّ الرفع.',
    tone: 'warning',
  },
  ASSETS_UPLOAD_TOO_LARGE: {
    message: 'حجم الصورة يتجاوز الحدّ المذكور تحت زرّ الرفع. اختر ملفّاً أصغر.',
    tone: 'warning',
  },
  // The current artwork was uploaded by an operator, so a provider fill will not
  // replace it — a refusal on WHAT IS STORED, not on the file just chosen.
  ASSETS_ARTWORK_OPERATOR_OWNED: {
    message: 'الصورة الحالية رفعها مشغّل ولن تُستبدل تلقائياً. أزلها أوّلاً ثمّ أعد الرفع.',
    tone: 'warning',
  },
  ASSETS_SUBJECT_TYPE_UNKNOWN: {
    message: 'تعذّر ربط الصورة بهذا المقال. أعد تحميل الصفحة ثمّ أعد الرفع.',
    tone: 'warning',
  },
  ASSETS_ARTWORK_PURPOSE_UNKNOWN: {
    message: 'تعذّر ربط الصورة بهذا المقال. أعد تحميل الصفحة ثمّ أعد الرفع.',
    tone: 'warning',
  },
  ASSETS_SOURCE_OBJECT_MISSING: {
    message: 'لم تصل الصورة إلى التخزين. أعد الرفع.',
    tone: 'warning',
  },
  ASSETS_FORBIDDEN: {
    message: 'رفع الصور خارج صلاحيّتك. راجع مسؤولاً.',
    tone: 'warning',
  },
  ASSETS_RATE_LIMITED: {
    message: 'رفعٌ كثير في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  ASSETS_UPSTREAM_FAILED: {
    message: 'خدمة الصور غير متاحة الآن. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  ASSETS_UPSTREAM_TIMEOUT: {
    message: 'تأخّرت خدمة الصور عن الردّ. أعد تحميل الصفحة للاطّلاع على النتيجة.',
    tone: 'neutral',
  },
};
